import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeSlash, ArrowRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { LOGO_URL } from '../lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center px-4 court-pattern">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Invalid reset link</h1>
        <Link to="/forgot-password" className="text-[#F97316] hover:text-orange-700 font-semibold">Request a new link</Link>
      </div>
    </div>
  );

  return (
    <>
      <SEO title="Reset Password" description="Set a new password for your Beyond Ball India account." path="/reset-password" />
      <div className="min-h-screen flex items-center justify-center px-4 py-24 court-pattern">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Beyond Ball India" className="h-14 w-14 rounded-full object-contain mx-auto" />
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-2" data-testid="reset-password-title">New password</h1>
            <p className="text-gray-600">Choose a strong password you'll remember.</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            {error && <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 mb-5 text-sm rounded-xl" data-testid="reset-error">{error}</div>}
            {success ? (
              <div className="text-center py-6">
                <div className="text-green-600 text-xl font-bold mb-2">Password reset!</div>
                <p className="text-gray-500 text-sm">Redirecting to login...</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">New password</label>
                  <div className="relative">
                    <input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="new-password-input"
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 pr-12 text-sm outline-none transition-all" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 p-1">
                      {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm password</label>
                  <input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input"
                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-3 text-sm outline-none transition-all" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="reset-submit-btn">
                  {loading ? 'Resetting...' : 'Reset password'} {!loading && <ArrowRight size={16} weight="bold" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
